import { MediaInfo } from "@coxauto-ui/cxm/communications/api";
import {
  EndlinkMediaMap,
  MediaItem
} from "@coxauto-ui/cxm/communications/interfaces";

// ---------------------------------------------------------------------------
// buildMediaWidget — editor-time HTML for a media widget node
// ---------------------------------------------------------------------------

const ALLOWED_PROTOCOLS = /^https?:\/\//i;

/**
 * Builds the stored HTML for a media widget — a `<span data-media-widget>`
 * containing one `<img>` per selected item.
 *
 * ProseMirror parses this as an atomic `mediaWidget` node (atom: true), so
 * the whole widget is selected and deleted as a single unit. At send time,
 * `wrapMediaWidget` locates the span and replaces it with an email-safe table.
 */
export function buildMediaWidget(
  items: MediaItem[],
  endlinkUrl: string
): string {
  if (!ALLOWED_PROTOCOLS.test(endlinkUrl)) return "";

  const doc = document;

  const span = doc.createElement("span");
  span.setAttribute("data-media-widget", endlinkUrl);
  span.style.cssText = "display:inline-block;";

  for (const m of items) {
    const imgSrc = m.previewUrl ?? m.thumbnailUrl ?? "";
    if (imgSrc && !ALLOWED_PROTOCOLS.test(imgSrc)) continue;

    const img = doc.createElement("img");
    img.src = imgSrc;
    img.alt = m.title ?? "Media";
    img.setAttribute("data-media-id", m.id);
    img.setAttribute("data-media-title", m.title ?? "");
    img.style.cssText =
      "width:180px;height:101px;object-fit:cover;vertical-align:middle;";
    span.appendChild(img);
  }

  return span.outerHTML;
}

// ---------------------------------------------------------------------------
// buildMediaInfoFromContent — send-time reconciliation
// ---------------------------------------------------------------------------

/**
 * Extracts active endlink URLs from email HTML by parsing
 * `span[data-media-widget]` attributes (present before wrapMediaWidget runs).
 */
function getActiveEmailEndlinks(html: string): Set<string> {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  return new Set(
    Array.from(doc.querySelectorAll<HTMLElement>("[data-media-widget]"))
      .map(el => el.getAttribute("data-media-widget"))
      .filter((url): url is string => !!url)
  );
}

/**
 * Extracts active endlink URLs from text content by checking
 * which known endlink URLs are still present in the string.
 */
function getActiveTextEndlinks(
  text: string,
  endlinkMediaMap: EndlinkMediaMap
): Set<string> {
  return new Set(
    Object.keys(endlinkMediaMap).filter(url => {
      const idx = text.indexOf(url);
      if (idx === -1) return false;
      const after = text[idx + url.length];
      // Reject only if the next char could extend the URL; any other
      // character (punctuation, whitespace, EOF) counts as a terminator.
      return after === undefined || !/[a-zA-Z0-9/_-]/.test(after);
    })
  );
}

/**
 * Builds a MediaInfo payload by reconciling the actual message content
 * against the endlinkMediaMap. Only includes media IDs and tracking IDs
 * for endlinks that are still present in the content.
 *
 * This handles the case where a user deletes a media widget from the
 * email editor or removes an endlink URL from a text message — those
 * items won't be included in the send request.
 *
 * @param content  The raw message content (email HTML or text body)
 * @param endlinkMediaMap  Map of endlink URL → { mediaIds, trackingIds }
 * @param isEmail  True for email (parse DOM), false for text (string search)
 * @returns MediaInfo if any active endlinks found, undefined otherwise
 */
export function buildMediaInfoFromContent(
  content: string,
  endlinkMediaMap: EndlinkMediaMap,
  isEmail: boolean
): MediaInfo | undefined {
  if (!content || Object.keys(endlinkMediaMap).length === 0) return undefined;

  const activeUrls = isEmail
    ? getActiveEmailEndlinks(content)
    : getActiveTextEndlinks(content, endlinkMediaMap);

  if (activeUrls.size === 0) return undefined;

  const mediaIds: string[] = [];
  const mediaViewTrackingIds: string[] = [];
  const mediaItems: MediaInfo["mediaItems"] = [];

  for (const url of activeUrls) {
    const entry = endlinkMediaMap[url];
    if (entry) {
      mediaIds.push(...entry.mediaIds);
      mediaViewTrackingIds.push(...entry.trackingIds);
      // One mediaItem per endlink — titles joined when an endlink covers multiple items.
      const title = entry.titles.filter(Boolean).join(", ");
      mediaItems.push({ title, endLink: url });
    }
  }

  return mediaIds.length > 0
    ? { mediaIds, mediaViewTrackingIds, mediaItems }
    : undefined;
}
